'use client';
import React, { useEffect, useRef, useState } from 'react';

const faqs = [
  {
    id: 'faq-1',
    question: 'What exactly is a prop trading challenge?',
    answer: 'Trade a simulated account against profit targets and risk limits. Pass → get funded with real capital. Keep 80% of profits. We handle everything else.',
  },
  {
    id: 'faq-2',
    question: 'How does the 80% profit split work in practice?',
    answer: 'You keep 80%, we keep 20%. Payouts in 24 hours. No minimums after first withdrawal. No earning caps.',
  },
  {
    id: 'faq-3',
    question: 'What happens if I breach a drawdown rule?',
    answer: 'Account deactivates immediately. Email breach report sent. Restart anytime—no waiting, no penalties.',
  },
  {
    id: 'faq-4',
    question: 'Are Expert Advisors (EAs) and automated strategies allowed?',
    answer: 'Yes! EAs and algorithms welcome. Copy trading from external signals only restriction.',
  },
  {
    id: 'faq-5',
    question: 'How quickly are payouts processed?',
    answer: 'Within 24 hours. Bank transfer, crypto, or e-wallet. First withdrawal needs 14 active trading days.',
  },
  {
    id: 'faq-6',
    question: 'Is there a time limit to pass the evaluation?',
    answer: 'No time limit. Trade at your pace. Minimum trading days required, but unlimited calendar time.',
  },
  {
    id: 'faq-7',
    question: 'How does the scaling plan work?',
    answer: '3 profitable months → 3% monthly balance increase. Capital and drawdowns scale together. No ceiling.',
  },
  {
    id: 'faq-8',
    question: 'How do I verify an HiyaPrime certificate?',
    answer: 'Unique ID + QR code on every certificate. Verify instantly at HiyaPrime.com/verify. Recruiters, employers, anyone can check.',
  },
];

function FAQItem({ faq, index }: { faq: typeof faqs[0]; index: number }) {
  return (
    <div className="card-elevated p-6 flex flex-col md:flex-row gap-6">
      <div className="md:w-1/3 flex-shrink-0">
        <h3 className="text-base font-bold text-foreground leading-snug">{faq.question}</h3>
      </div>
      <div className="md:w-2/3 flex-grow">
        <p className="text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="faq" className="py-14 md:py-28">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 2xl:px-16">
        <div className="max-w-5xl mx-auto">
          <div
            ref={ref}
            className="text-center mb-14 space-y-4"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.7s ease, transform 0.7s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-muted text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              Common Questions
            </div>
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-extrabold text-foreground">
              Everything You Need to{' '}
              <span className="text-gradient-crimson">Know</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-relaxed">
              Straight answers to the questions traders ask most before starting their first challenge.
            </p>
          </div>

          <div
            className="grid gap-4"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
            }}
          >
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.id}
                faq={faq}
                index={i}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
